<template>
  <nav class="px-4 py-4 bg-white relative z-[1000]">
    <div class="mx-auto flex items-center justify-between gap-5">
      <a class="text-[22px] font-semibold text-gray-900 tracking-tight select-none whitespace-nowrap" href="#">
        Accounting System
      </a>

      <!-- DESKTOP (1024px+) -->
      <div class="hidden lg:flex items-center gap-3 flex-1 justify-end">
        <div class="h-14 rounded-[23px] ring-1 ring-[#c9d5df] shadow-sm bg-gradient-to-b from-[#d9e8f2] to-[#c3d2de] flex items-center">
          <ul class="flex items-center gap-3 px-5">
            <li
              v-for="(item, i) in filteredItems"
              :key="item.key"
              class="relative"
              @mouseenter="hasLinks(item) ? onItemEnter(i) : null"
              @mouseleave="hasLinks(item) ? onItemLeave(i) : null"
            >
              <template v-if="!hasLinks(item)">
                <router-link :to="linkFor(item)" class="nav-link" @click="closeAll">{{ item.label }}</router-link>
              </template>
              <template v-else>
                <button type="button" class="nav-link" @click.stop="toggleItem(i)" :aria-expanded="openIndex === i">
                  {{ item.label }}
                  <svg class="ml-1 h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <ul v-show="openIndex === i" class="dropdown" @mouseenter="cancelClose(i)" @mouseleave="scheduleClose(i)">
                  <li v-for="link in visibleLinks(item.links)" :key="link.href">
                    <router-link :to="link.href || '#'" class="dropdown-item" @click="closeAll">{{ link.text }}</router-link>
                  </li>
                </ul>
              </template>
            </li>
          </ul>
        </div>

        <!-- Profile pill (desktop) -->
        <div class="relative" @mouseenter="onProfileEnter" @mouseleave="onProfileLeave">
          <button type="button" class="profile-pill" @mouseup.stop="profileOpen = !profileOpen" :aria-expanded="profileOpen">
            <svg class="h-5 w-5 text-gray-800" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 12a4.5 4.5 0 1 0-4.5-4.5A4.51 4.51 0 0 0 12 12Z"/>
              <path d="M4.5 20.5a7.5 7.5 0 0 1 15 0"/>
            </svg>
            <svg class="ml-2 h-4 w-4 text-gray-800 transition-transform duration-150" :class="profileOpen ? 'rotate-180' : ''" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          <div
            v-show="profileOpen"
            class="dropdown lg:right-5 lg:top-[70px] w-36 rounded-xl py-3 text-center z-50"
            @mouseenter="cancelProfileClose"
            @mouseleave="scheduleProfileClose"
          >
            <div class="px-2 pb-2 text-[13px] font-semibold text-gray-900 select-none">
              {{ userName || 'User Name' }}
            </div>
            <button type="button" class="menu-row-center" @click="toggleLang">
              <svg viewBox="0 0 24 24" class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7h8M8 7v10M5 12h6" /><path d="M16.5 17l2.5-7 2.5 7M17.3 15h2.4" /></svg>
              <span class="ml-1">{{ lang }}</span>
            </button>
            <button type="button" class="menu-row-center" @click="logout">
              <svg viewBox="0 0 24 24" class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 12H3" /><path d="M11 8l-4 4 4 4" /><path d="M15 4h5v16h-5" /></svg>
              <span class="ml-1">Log Out</span>
            </button>
          </div>
        </div>
      </div>

      <!-- TABLET (640px - 1023px) -->
      <div class="hidden md:flex lg:hidden items-center gap-2 flex-1 justify-end min-w-0">
        <div class="h-11 rounded-full ring-1 ring-[#c9d5df] shadow-sm bg-gradient-to-b from-[#d9e8f2] to-[#c3d2de] flex items-center overflow-x-auto no-scrollbar min-w-0 flex-1">
          <ul class="flex items-center gap-3 px-4 min-w-max">
            <li v-for="(item, i) in filteredItems" :key="item.key" class="relative">
              <template v-if="!hasLinks(item)">
                <router-link :to="linkFor(item)" class="nav-link-sm" @click="closeAll">{{ item.label }}</router-link>
              </template>
              <template v-else>
                <button type="button" class="nav-link-sm" @click.stop="toggleItem(i)">
                  {{ item.label }}
                  <svg class="ml-0.5 h-3 w-3 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" /></svg>
                </button>
                <ul v-show="openIndex === i" class="dropdown" @mouseenter="cancelClose(i)" @mouseleave="scheduleClose(i)">
                  <li v-for="link in visibleLinks(item.links)" :key="link.href">
                    <router-link :to="link.href || '#'" class="dropdown-item" @click="closeAll">{{ link.text }}</router-link>
                  </li>
                </ul>
              </template>
            </li>
          </ul>
        </div>
        <div class="relative shrink-0" @mouseenter="onProfileEnter" @mouseleave="onProfileLeave">
          <div class="h-11 w-11 rounded-full ring-1 ring-[#c9d5df] shadow-sm bg-gradient-to-b from-[#d9e8f2] to-[#c3d2de] flex items-center justify-center">
            <button type="button" class="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/70 ring-1 ring-slate-200 shadow-sm hover:bg-white transition" @click="profileOpen = !profileOpen">
              <svg class="h-4 w-4 text-gray-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 12a4.5 4.5 0 1 0-4.5-4.5A4.51 4.51 0 0 0 12 12Z"/>
                <path d="M4.5 20.5a7.5 7.5 0 0 1 15 0"/>
              </svg>
            </button>
          </div>
          <div
            v-show="profileOpen"
            class="dropdown md:right-2 md:top-[60px] w-36 rounded-xl py-3 text-center z-50"
            @mouseenter="cancelProfileClose"
            @mouseleave="scheduleProfileClose"
          >
          <div class="px-2 pb-2 text-[13px] font-semibold text-gray-900 select-none">
              {{ userName || 'User Name' }}
            </div>
            <button type="button" class="menu-row-center" @click="toggleLang">
              <svg viewBox="0 0 24 24" class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7h8M8 7v10M5 12h6" /><path d="M16.5 17l2.5-7 2.5 7M17.3 15h2.4" /></svg>
              <span class="ml-1">{{ lang }}</span>
            </button>
            <button type="button" class="menu-row-center" @click="logout">
              <svg viewBox="0 0 24 24" class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 12H3" /><path d="M11 8l-4 4 4 4" /><path d="M15 4h5v16h-5" /></svg>
              <span class="ml-1">Log Out</span>
            </button>
          </div>
        </div>
      </div>

      <!-- MOBILE: hamburger -->
      <button class="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-xl ring-1 ring-slate-200 hover:bg-slate-100 shrink-0" @click="menuOpen = !menuOpen" aria-label="Toggle navigation">
        <svg class="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path stroke-linecap="round" stroke-width="2" d="M4 7h16M4 12h16M4 17h16" />
        </svg>
      </button>
    </div>

    <!-- Mobile drawer -->
    <div v-if="menuOpen" class="sm:hidden mt-3">
      <div class="rounded-2xl p-3 bg-gradient-to-b from-[#d9e8f2] to-[#c3d2de] ring-1 ring-[#c9d5df] shadow-sm">
        <div class="grid gap-2">
          <div v-for="item in filteredItems" :key="item.key">
            <template v-if="!hasLinks(item)">
              <router-link :to="linkFor(item)" class="mobile-link block" @click="menuOpen = false; closeAll()">
                {{ item.label }}
              </router-link>
            </template>
            <template v-else>
              <button
                type="button"
                class="mobile-link w-full text-left flex items-center justify-between"
                @click="toggleMobileGroup(item.key)"
              >
                <span>{{ item.label }}</span>
                <svg class="h-4 w-4 transition-transform duration-150" :class="mobileOpenKey === item.key ? 'rotate-180' : ''" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
              <div v-if="mobileOpenKey === item.key" class="mt-1 ml-3 grid gap-1">
                <router-link
                  v-for="link in visibleLinks(item.links)"
                  :key="link.href"
                  :to="link.href || '#'"
                  class="mobile-link text-[13px]"
                  @click="menuOpen = false; mobileOpenKey = null; closeAll()"
                >
                  {{ link.text }}
                </router-link>
              </div>
            </template>
          </div>
          <button @click="logout" class="mobile-link text-left bg-red-50 text-red-700 font-semibold">Logout</button>
        </div>
      </div>
    </div>
  </nav>
</template>

<script>

import { loadMyPermissions, hasAny, permissionsVersion } from "@/utils/permissions";

export default {
  name: "Navbar",
  data() {
    return {
      permissionsVersion,   // reactive ref from permissions module
      permissionsReady: false,
      menuOpen:     false,
      profileOpen:  false,
      openIndex:    null,
      mobileOpenKey: null,
      timers:       {},
      openDelay:    80,
      closeDelay:   220,
      lang:         "EN",
      profileTimer: null,
      userName:     localStorage.getItem("user_name") || "User",

      REQUIRES: {
        "/Dashboard":                        "dashboard.view",
        "/payments":                         "payments.payments.view",
        "/banks":                            "payments.banks.view",
        "/accounting-tree":                  "accounting_tree.view",
        "/purchasing-fund":                  "procurement_management.purchasing_fund.view",
        "/procurement":                      "procurement_management.procurement.view",
        "/registration-deeds":               "registration_deed.view",
        "/checks":                           "check.view",
        "/reports/tax-report":               "tax_reports.view",
        "/reports/accountTree-report":       "account_tree_report.view",
        "/reports/procurements":             "procurements_report.view",
        "/reports/trialBalance":             "trial_balance_report.view",
        "/reports/payments":                 "payments_report.view",
        "/reports/check":                    "check_report.view",
        "/reports/purchasing-fund-tabs":     "purchasing_fund_tabs_report.view",
        "/reports/income-statement":         "income_statement_report.view",
        "/reports/customer-account-statements": "customer_account_statements_report.view",
        "/reports/suppliers":                "suppliers_report.view",
        "/permissions":                      "others.permissions",
        "/suppliers/company":                "others.suppliers",
        "/companies":                        "others.my_companies",
        "/login-image":                      "others.view",
      },

      items: [
        { key: "dashboard", label: "Dashboard", href: "/Dashboard" },
        {
          key: "Payments", label: "Payments",
          links: [{ href: "/payments", text: "Payments" }, { href: "/banks", text: "Banks" }],
        },
        { key: "Accounting Tree", label: "Accounting Tree", href: "/accounting-tree" },
        {
          key: "Procurement Management", label: "Procurement Management",
          links: [{ href: "/purchasing-fund", text: "Purchasing Fund" }, { href: "/procurement", text: "Procurement" }],
        },
        { key: "Registration Deed", label: "Registration Deed", href: "/registration-deeds" },
        { key: "Check", label: "Check", href: "/checks" },
        {
          key: "Reports", label: "Reports",
          links: [
            { href: "/reports/tax-report",           text: "Tax Report"                  },
            { href: "/reports/accountTree-report",   text: "Account Tree Report"         },
            { href: "/reports/procurements",         text: "Procurements Report"         },
            { href: "/reports/trialBalance",         text: "Trial Balance Report"        },
            { href: "/reports/payments",             text: "Payments Report"             },
            { href: "/reports/check",                text: "Check Report"                },
            { href: "/reports/purchasing-fund-tabs", text: "Purchasing Fund Tabs Report" },
            { href: "/reports/income-statement",     text: "Income Statement Report"     },
            { href: "/reports/customer-account-statements", text: "Customer Account Statements" },
            { href: "/reports/suppliers",            text: "Suppliers Report" },
          ],
        },
        {
          key: "Others", label: "Others",
          links: [
            { href: "/permissions",        text: "Permissions"   },
            { href: "/suppliers/company", text: "Suppliers"    },
            { href: "/companies",         text: "My Companies" },
            { href: "/login-image",        text: "Edit Login Image" },

          ],
        },
      ],
    };
  },

  computed: {
    filteredItems() {
      // depend on the reactive version counter so this recomputes whenever permissions load
      void this.permissionsVersion.value;
      return this.items.filter((item) => this.isItemVisible(item));
    },
  },

  methods: {
    permOk(href) {
      const need = this.REQUIRES[href];
      if (!need) return true;
      return Array.isArray(need) ? hasAny(need) : hasAny([need]);
    },

    visibleLinks(links) {
      return (links || []).filter((l) => this.permOk(l.href));
    },

    isItemVisible(item) {
      if (this.hasLinks(item)) return this.visibleLinks(item.links).length > 0;
      return this.permOk(item.href);
    },

    hasLinks(item) { return Array.isArray(item.links) && item.links.length > 0; },

    linkFor(item) {
      if (typeof item.href !== "undefined") return item.href || "#";
      if (this.hasLinks(item)) {
        const first = this.visibleLinks(item.links)[0];
        return first ? first.href : "#";
      }
      return "#";
    },

    toggleMobileGroup(key) { this.mobileOpenKey = this.mobileOpenKey === key ? null : key; },
    closeAll() { this.openIndex = null; this.profileOpen = false; },
    clearTimer(i)    { if (this.timers[i]) { clearTimeout(this.timers[i]); this.timers[i] = null; } },
    toggleLang()     { this.lang = this.lang === "EN" ? "AR" : "EN"; },
    openItem(i)      { if (this.openIndex !== i) this.openIndex = i; },
    closeItem(i)     { if (this.openIndex === i) this.openIndex = null; },
    toggleItem(i)    { this.clearTimer(i); this.openIndex = this.openIndex === i ? null : i; },
    onItemEnter(i)   { this.clearTimer(i); this.timers[i] = setTimeout(() => this.openItem(i), this.openDelay); },
    onItemLeave(i)   { this.scheduleClose(i); },
    cancelClose(i)   { this.clearTimer(i); },
    scheduleClose(i) { this.clearTimer(i); this.timers[i] = setTimeout(() => this.closeItem(i), this.closeDelay); },
    onProfileEnter()       { this.cancelProfileClose(); this.profileOpen = true; },
    onProfileLeave()       { this.scheduleProfileClose(); },
    scheduleProfileClose() { this.clearProfileTimer(); this.profileTimer = setTimeout(() => (this.profileOpen = false), this.closeDelay); },
    cancelProfileClose()   { this.clearProfileTimer(); },
    clearProfileTimer()    { if (this.profileTimer) { clearTimeout(this.profileTimer); this.profileTimer = null; } },

    async logout() {
      const token = localStorage.getItem("token");
      const slug  = localStorage.getItem("company_id");
      try {
        await fetch("https://accounting.lyon-jo.com/api/logout", {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
      } catch (e) {
        console.error("Logout API failed:", e);
      } finally {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        localStorage.removeItem("user_id");
        localStorage.removeItem("user_name");
        localStorage.removeItem("perm_ready");
        localStorage.removeItem("perm_granted");
        localStorage.removeItem("perm_user_id");
        window.location.href = slug ? `/login/${slug}` : "/not-found";
      }
    },

    onDocClick(e) { if (!this.$el.contains(e.target)) this.closeAll(); },
    onEsc(e) { if (e.key === "Escape") { this.closeAll(); this.menuOpen = false; this.mobileOpenKey = null; } },
  },

  mounted() {
    // Permissions are already loaded by App.vue before Navbar mounts.
    // permissionsVersion ref is reactive — filteredItems is correct on first render.
    this.permissionsReady = true;
    document.addEventListener("click", this.onDocClick);
    document.addEventListener("keydown", this.onEsc);
  },

  beforeUnmount() {
    document.removeEventListener("click", this.onDocClick);
    document.removeEventListener("keydown", this.onEsc);
  },
};
</script>

<style scoped>
.nav-link     { @apply inline-flex items-center text-[13px] font-medium text-gray-800 hover:text-gray-900 transition whitespace-nowrap; }
.nav-link-sm  { @apply inline-flex items-center text-[12px] font-medium text-gray-800 hover:text-gray-900 transition whitespace-nowrap; }
.dropdown {
  @apply min-w-44 rounded-xl shadow-lg ring-1 ring-slate-100 py-2;
  position: fixed;
  z-index: 9999;
  background: linear-gradient(to top, #dee0e3 0%, #f9fafb 60%, #ffffff 100%);
}
.mobile-link  { @apply px-3 py-2 rounded-lg bg-white/80 ring-1 ring-slate-200 hover:bg-white text-gray-900 font-medium; }
.no-scrollbar::-webkit-scrollbar { display: none; }
.no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
.menu-row-center { @apply w-full flex items-center justify-center gap-2 px-2 py-2.5 text-sm text-gray-900 rounded-md transition; }
.dropdown-item { @apply block px-4 py-2 text-sm text-gray-700 rounded-md transition; }
.dropdown-item:hover { background-color: #709ebd; color: #fff; }
.menu-row-center:hover { background-color: #709ebd !important; color: #fff !important; }
.profile-pill { @apply h-14 px-4 inline-flex items-center rounded-[23px] ring-1 ring-[#c9d5df] shadow-sm transition select-none; background: linear-gradient(to bottom, #d9e8f2, #c3d2de); }
.profile-pill:hover { filter: brightness(1.02); }
</style>
